import { COMMON_FEEDBACK_CATEGORIES } from 'utlis/constants';

import { TEMPLATE_TITLES } from './constants';

export const SEVERITY_COLORS = {
  ESSENTIAL: '#DC3412',
  CAUTION: '#B86200',
  NEUTRAL: '#00000080',
  POSITIVE: '#1B988E',
  DEEPER_INSIGHTS: '#7C2BDA',
};

// Severity hierarchy for priority determination (higher number = higher priority)
const FEEDBACK_SEVERITY_PRIORITY = {
  'deeper-insights': 4,
  error: 3,
  suggestion: 2,
  positive: 1,
};

// Color mapping for different feedback severities (matching ImageCarousel logic)
const FEEDBACK_SEVERITY_COLORS = {
  'deeper-insights': '#9061F9', // Purple
  error: '#EF2A35', // Red
  suggestion: '#C57C00', // Yellow/Orange
  positive: '#10B981', // Green
};

// Utility function to determine the color for a category based on its feedback severity
export const getCategoryColor = categoryFeedback => {
  if (!Array.isArray(categoryFeedback) || categoryFeedback.length === 0) {
    return FEEDBACK_SEVERITY_COLORS.positive; // Default to green for no findings
  }

  let highestSeverity = 'positive';
  let highestPriority = 0;

  categoryFeedback.forEach(feedback => {
    const getCategory = item => {
      if (item.isDeeperInsight) {
        return 'deeper-insights';
      }

      return item.category || 'positive';
    };

    const category = getCategory(feedback);
    const priority = FEEDBACK_SEVERITY_PRIORITY[category] || 0;

    if (priority > highestPriority) {
      highestPriority = priority;
      highestSeverity = category;
    }
  });

  return FEEDBACK_SEVERITY_COLORS[highestSeverity] || FEEDBACK_SEVERITY_COLORS.positive;
};

const SORT_PRIORITY = {
  POSITIVE: 0,
  ESSENTIAL: 1,
  CAUTION: 2,
  NEUTRAL: 3,
};

// Function to get the highest severity color for a category
const getCategorySeverity = (feedbackItems, categoryKey, reviewType) => {
  if (categoryKey === 'ALT_TEXT') {
    return SEVERITY_COLORS.NEUTRAL; // Alt Text always gets neutral color
  }

  if (categoryKey === 'DEEPER_INSIGHTS') {
    return SEVERITY_COLORS.DEEPER_INSIGHTS; // Deeper Insights always gets purple color
  }

  const categories = new Set(feedbackItems.map(({ category }) => category));

  if (reviewType === 'audit ui' && categories.has('positive') && categories.size === 1) {
    return SEVERITY_COLORS.POSITIVE;
  }

  return categories.has('error')
    ? SEVERITY_COLORS.ESSENTIAL
    : categories.has('suggestion')
      ? SEVERITY_COLORS.CAUTION
      : categories.has('positive')
        ? SEVERITY_COLORS.POSITIVE
        : SEVERITY_COLORS.NEUTRAL;
};

const sortCategories = categories => categories.sort((a, b) => {
  // Deeper Insights always at the top
  if (a.categoryKey === 'DEEPER_INSIGHTS' || b.categoryKey === 'DEEPER_INSIGHTS') {
    return a.categoryKey === 'DEEPER_INSIGHTS' ? -1 : 1;
  }

  // Positive-only categories always at the top (Audit UI only)
  if (a.isPositiveOnly !== b.isPositiveOnly) {
    return b.isPositiveOnly ? 1 : -1;
  }

  // Then sort by severity (Essential > Caution > Neutral)
  if (a.categoryKey === 'ALT_TEXT' || b.categoryKey === 'ALT_TEXT') {
    return a.categoryKey === 'ALT_TEXT' ? 1 : -1;
  }
  const hasEssentialA = a.categoryFeedback.some(item => item.category === 'error');
  const hasEssentialB = b.categoryFeedback.some(item => item.category === 'error');

  if (hasEssentialA !== hasEssentialB) {
    return hasEssentialB ? 1 : -1;
  }

  // Others category should go last if its findings count matches Essential or Caution categories
  if (a.categoryKey === 'OTHERS' || b.categoryKey === 'OTHERS') {
    return a.categoryFeedback.length === b.categoryFeedback.length
      ? a.categoryKey === 'OTHERS' ? 1 : -1
      : b.categoryFeedback.length - a.categoryFeedback.length;
  }

  // Only Caution findings (Sort by number of findings in descending order)
  const hasOnlyCautionA = a.categoryFeedback.every(item => item.category === 'suggestion');
  const hasOnlyCautionB = b.categoryFeedback.every(item => item.category === 'suggestion');

  if (hasOnlyCautionA && hasOnlyCautionB) {
    return b.categoryFeedback.length - a.categoryFeedback.length;
  }

  // Fallback sorting: Sort by severity, then by number of feedback items (descending)
  return (SORT_PRIORITY[a.severityColor] ?? 4) - (SORT_PRIORITY[b.severityColor] ?? 4)
         || b.categoryFeedback.length - a.categoryFeedback.length;
});

const normalizeString = str => str.replace(/_/g, ' ').toLowerCase();

const initializeCategory = (categories, categoryKey) => {
  categories[categoryKey] = {
    categoryName: COMMON_FEEDBACK_CATEGORIES[categoryKey].categoryName,
    categoryIcon: COMMON_FEEDBACK_CATEGORIES[categoryKey].icon,
    categoryFeedback: [],
  };
};

const categorizeAccessibility = feedbackItems => {
  const categories = {};
  const colorContrastIssueIdentifier = 'Accessibility : Font Size Issue';

  feedbackItems.forEach((feedback, index) => {
    const feedbackWithIndex = { ...feedback, index };
    const label = normalizeString(feedback.label || '');

    if (feedback?.color_suggestions) {
      !categories.COLOR_CONTRAST && initializeCategory(categories, 'COLOR_CONTRAST');
      categories.COLOR_CONTRAST.categoryFeedback.push(feedbackWithIndex);
    }
    else if (feedback?.font_size && feedback.issue === colorContrastIssueIdentifier) {
      !categories.TYPOGRAPHY_SIZE && initializeCategory(categories, 'TYPOGRAPHY_SIZE');
      categories.TYPOGRAPHY_SIZE.categoryFeedback.push(feedbackWithIndex);
    }
    else if (feedback?.width && feedback?.height) {
      !categories.TARGET_SIZE && initializeCategory(categories, 'TARGET_SIZE');
      categories.TARGET_SIZE.categoryFeedback.push(feedbackWithIndex);
    }
    else if (feedback?.alt_text) {
      !categories.ALT_TEXT && initializeCategory(categories, 'ALT_TEXT');
      categories.ALT_TEXT.categoryFeedback.push(feedbackWithIndex);
    }
    else if (label === COMMON_FEEDBACK_CATEGORIES['OVERVIEW']?.categoryName.toLowerCase()) {
      !categories.OVERVIEW && initializeCategory(categories, 'OVERVIEW');
      categories.OVERVIEW.categoryFeedback.push(feedbackWithIndex);
    }
  });

  return categories;
};

const handlePositiveFeedback = (feedback, categories) => {
  const positiveFeedbackMapping = {
    color: 'COLOR',
    typography: 'TYPOGRAPHY',
    layoutAndHierarchy: 'LAYOUT',
  };

  Object.entries(positiveFeedbackMapping).forEach(([feedbackKey, categoryKey]) => {
    if (feedback?.[feedbackKey] && feedback[feedbackKey].length > 0) {
      if (!categories[categoryKey]) {
        initializeCategory(categories, categoryKey);
      }
      categories[categoryKey].categoryFeedback.push({
        ...feedback,
        positiveCategory: feedbackKey,
      });
    }
  });
};

// Categorize feedback items based on label
const categorizeFeedbackByLabel = (feedbackItems, reviewType) => {
  const categories = {};
  const isUserJourneys = reviewType === TEMPLATE_TITLES.USER_JOURNEYS.toLowerCase();

  feedbackItems.forEach((feedback, index) => {
    const label = normalizeString(feedback.label || '');
    const feedbackWithIndex = { ...feedback, index };

    // Check for deeper insights by category property
    if (feedback.category === 'deeper_insight' || feedback.isDeeperInsight) {
      if (!categories['DEEPER_INSIGHTS']) {
        initializeCategory(categories, 'DEEPER_INSIGHTS');
      }
      categories['DEEPER_INSIGHTS'].categoryFeedback.push({
        ...feedbackWithIndex,
        isDeeperInsight: true,
      });

      return;
    }

    if (reviewType === 'audit ui' && feedback.category === 'positive') {
      handlePositiveFeedback(feedbackWithIndex, categories);

      return;
    }

    // Find matching category
    const matchedCategory = Object.entries(COMMON_FEEDBACK_CATEGORIES).find(
      ([key]) => normalizeString(key) === label
    );

    // For User journeys, keep original category name if not in COMMON_FEEDBACK_CATEGORIES
    // Otherwise use the matched category or OTHERS as fallback
    let categoryKey;

    if (isUserJourneys && !matchedCategory && label !== '') {
      // Create a unique key from the label
      categoryKey = label.toUpperCase().replace(/\s+/g, '_');
    }
    else {
      categoryKey = matchedCategory ? matchedCategory[0] : 'OTHERS';
    }

    if (!categories[categoryKey]) {
      // For User journeys, set icon to null
      // For custom categories in User journeys, use label as category name
      const isCustomCategory = isUserJourneys && !matchedCategory && label !== '';

      categories[categoryKey] = {
        categoryName: isCustomCategory
          // Capitalize first letter of each word for better display
          ? label.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
          : COMMON_FEEDBACK_CATEGORIES[categoryKey]?.categoryName || 'Others',
        categoryIcon: isUserJourneys
          ? null
          : (COMMON_FEEDBACK_CATEGORIES[categoryKey]?.icon || COMMON_FEEDBACK_CATEGORIES.OTHERS.icon),
        categoryFeedback: [],
      };
    }

    categories[categoryKey].categoryFeedback.push(feedbackWithIndex);
  });

  // Only add OTHERS category if we're not in User journeys mode and it doesn't exist yet
  if (!isUserJourneys && !categories['OTHERS']) {
    categories['OTHERS'] = {
      categoryName: COMMON_FEEDBACK_CATEGORIES['OTHERS']?.categoryName,
      categoryIcon: COMMON_FEEDBACK_CATEGORIES['OTHERS']?.icon,
      categoryFeedback: [],
    };
  }

  return categories;
};

export const categorizeFeedback = (feedbackItems, reviewType, currentRecord = null) => {
  reviewType = reviewType?.toLowerCase();

  const categories = reviewType === 'accessibility'
    ? categorizeAccessibility(feedbackItems)
    : categorizeFeedbackByLabel(feedbackItems, reviewType);

  // Check for deeper insights in extra_data
  if (
    currentRecord?.extra_data?.post_processes?.deeper_insights?.status === 2
      && currentRecord?.extra_data?.post_processes?.deeper_insights?.feedback?.length > 0
  ) {
    const deeperInsightsFeedback = currentRecord.extra_data.post_processes.deeper_insights.feedback;

    categories['DEEPER_INSIGHTS'] = {
      categoryName: COMMON_FEEDBACK_CATEGORIES['DEEPER_INSIGHTS'].categoryName,
      categoryIcon: COMMON_FEEDBACK_CATEGORIES['DEEPER_INSIGHTS'].icon,
      categoryFeedback: deeperInsightsFeedback.map((insight, index) => ({
        ...insight,
        index: currentRecord.final_feedback.length + index,
        isDeeperInsight: true,
      })),
    };
  }

  Object.entries(categories).forEach(([categoryKey, value]) => {
    value.severityColor = getCategorySeverity(value.categoryFeedback, categoryKey, reviewType);
    value.isPositiveOnly = reviewType === 'audit ui' && value.severityColor === SEVERITY_COLORS.POSITIVE;
  });

  return sortCategories(
    Object.entries(categories)
      .filter(([, value]) => value.categoryFeedback.length > 0)
      .map(([key, value]) => ({
        categoryKey: key,
        categoryName: value.categoryName,
        categoryIcon: value.categoryIcon,
        categoryFeedback: value.categoryFeedback,
        severityColor: value.severityColor,
        isPositiveOnly: value.isPositiveOnly,
      }))
  );
};
